// src/pages/AdminBlog.js
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BlogContext } from '../BlogContext';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../AuthContext';

const AdminBlog = () => {
  const { posts, loading } = useContext(BlogContext);
  const { isAuthenticated } = useAuth();
  const [editingPost, setEditingPost] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [sortBy, setSortBy] = useState('newest');
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    publishedDate: '',
    headerImage: '',
    author: '',
    timeToRead: '',
    views: 0,
    visibility: 'public', // public, unlisted, private
    content: [],
    // SEO fields
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
  });

  // Update simple text fields
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // For updating a content section's field
  const handleSectionChange = (index, field, value) => {
    const newContent = [...formData.content];
    newContent[index] = {
      ...newContent[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  // Add a new content section
  const addSection = (type = 'text') => {
    const newSection = {
      type,
      title: '',
      ...(type === 'text' && { value: '' }),
      ...(type === 'image' && { fileName: '', alt: '', size: 'large', alignment: 'center' }),
      ...(type === 'list' && { items: [''] }),
      ...(type === 'table' && { 
        headers: ['Header 1', 'Header 2'], 
        rows: [['Cell 1', 'Cell 2'], ['Cell 3', 'Cell 4']] 
      }),
      ...(type === 'timeline' && {
        events: [{
          date: '',
          title: '',
          content: [{
            type: 'text',
            value: ''
          }]
        }]
      }),
    };
    
    setFormData({
      ...formData,
      content: [...formData.content, newSection],
    });
  };

  // Delete a content section
  const deleteSection = (index) => {
    const newContent = formData.content.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  // Move section up/down
  const moveSection = (index, direction) => {
    const newContent = [...formData.content];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newContent.length) {
      [newContent[index], newContent[targetIndex]] = [newContent[targetIndex], newContent[index]];
      setFormData({
        ...formData,
        content: newContent,
      });
    }
  };

  // Add a new bullet point to a list
  const addListItem = (sectionIndex) => {
    const newContent = [...formData.content];
    newContent[sectionIndex].items.push('');
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  // Remove a bullet point from a list
  const removeListItem = (sectionIndex, itemIndex) => {
    const newContent = [...formData.content];
    newContent[sectionIndex].items.splice(itemIndex, 1);
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  // Update a specific list item
  const updateListItem = (sectionIndex, itemIndex, value) => {
    const newContent = [...formData.content];
    newContent[sectionIndex].items[itemIndex] = value;
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  // Move a list item up or down
  const moveListItem = (sectionIndex, itemIndex, direction) => {
    const newContent = [...formData.content];
    const items = newContent[sectionIndex].items;
    const targetIndex = direction === 'up' ? itemIndex - 1 : itemIndex + 1;

    if (targetIndex >= 0 && targetIndex < items.length) {
      [items[itemIndex], items[targetIndex]] = [items[targetIndex], items[itemIndex]];
      setFormData({
        ...formData,
        content: newContent,
      });
    }
  };

  // Table editing functions
  const updateTableCell = (sectionIndex, rowIndex, colIndex, value, isHeader = false) => {
    const newContent = [...formData.content];
    if (isHeader) {
      newContent[sectionIndex].headers[colIndex] = value;
    } else {
      newContent[sectionIndex].rows[rowIndex][colIndex] = value;
    }
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  const addTableRow = (sectionIndex) => {
    const newContent = [...formData.content];
    const table = newContent[sectionIndex];
    const newRow = new Array(table.headers.length).fill('');
    table.rows.push(newRow);
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  const removeTableRow = (sectionIndex, rowIndex) => {
    const newContent = [...formData.content];
    newContent[sectionIndex].rows.splice(rowIndex, 1);
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  const addTableColumn = (sectionIndex) => {
    const newContent = [...formData.content];
    const table = newContent[sectionIndex];
    table.headers.push('New Header');
    table.rows.forEach(row => row.push(''));
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  const removeTableColumn = (sectionIndex, colIndex) => {
    const newContent = [...formData.content];
    const table = newContent[sectionIndex];
    table.headers.splice(colIndex, 1);
    table.rows.forEach(row => row.splice(colIndex, 1));
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  // Timeline editing functions
  const addTimelineEvent = (sectionIndex) => {
    const newContent = [...formData.content];
    newContent[sectionIndex].events.push({
      date: '',
      title: '',
      content: [{
        type: 'text',
        value: ''
      }]
    });
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  const removeTimelineEvent = (sectionIndex, eventIndex) => {
    const newContent = [...formData.content];
    newContent[sectionIndex].events.splice(eventIndex, 1);
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  const updateTimelineEvent = (sectionIndex, eventIndex, field, value) => {
    const newContent = [...formData.content];
    newContent[sectionIndex].events[eventIndex] = {
      ...newContent[sectionIndex].events[eventIndex],
      [field]: value,
    };
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  const addTimelineEventContent = (sectionIndex, eventIndex, type = 'text') => {
    const newContent = [...formData.content];
    const event = newContent[sectionIndex].events[eventIndex];
    const newContentSection = {
      type,
      ...(type === 'text' && { value: '' }),
      ...(type === 'image' && { fileName: '', alt: '', size: 'large', alignment: 'center' }),
      ...(type === 'list' && { items: [''] }),
    };
    event.content.push(newContentSection);
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  const updateTimelineEventContent = (sectionIndex, eventIndex, contentIndex, field, value) => {
    const newContent = [...formData.content];
    newContent[sectionIndex].events[eventIndex].content[contentIndex] = {
      ...newContent[sectionIndex].events[eventIndex].content[contentIndex],
      [field]: value,
    };
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  const removeTimelineEventContent = (sectionIndex, eventIndex, contentIndex) => {
    const newContent = [...formData.content];
    newContent[sectionIndex].events[eventIndex].content.splice(contentIndex, 1);
    setFormData({
      ...formData,
      content: newContent,
    });
  };

  // Convert section arrays to Firestore-safe JSON strings
  const prepareDataForFirestore = (formData) => {
    const processedContent = formData.content.map(section => {
      if (section.type === 'table' && Array.isArray(section.rows)) {
        return {
          ...section,
          headers: JSON.stringify(section.headers),
          rows: JSON.stringify(section.rows)
        };
      }
      if (section.type === 'list' && Array.isArray(section.items)) {
        return {
          ...section,
          items: JSON.stringify(section.items)
        };
      }
      if (section.type === 'timeline' && Array.isArray(section.events)) {
        const processedEvents = section.events.map(event => ({
          ...event,
          content: event.content.map(contentSection => {
            if (contentSection.type === 'list' && Array.isArray(contentSection.items)) {
              return {
                ...contentSection,
                items: JSON.stringify(contentSection.items)
              };
            }
            return contentSection;
          })
        }));
        return {
          ...section,
          events: JSON.stringify(processedEvents)
        };
      }
      return section;
    });

    return {
      ...formData,
      content: processedContent
    };
  };

  // Converts stored strings (JSON / legacy) back to arrays for editor/preview
  const toArray = (input, isTable = false) => {
    if (Array.isArray(input)) return input;
    if (typeof input !== 'string') return [];

    if (input.trim().startsWith('[')) {
      try { return JSON.parse(input); } catch { /* ignore */ }
    }

    // legacy
    if (!isTable) {
      const delimiter = input.includes('|') ? '|' : ',';
      return input.split(delimiter).map(safeBase64Decode).map(s => s.trim());
    } else {
      const rowDelimiter = input.includes('||') ? '||' : ';';
      const cellDelimiter = input.includes('|') && !input.includes('||') ? '|' : ',';
      return input.split(rowDelimiter).map(r => r.split(cellDelimiter).map(c => safeBase64Decode(c.trim())));
    }
  };

  // Helper function to safely decode Base64
  const safeBase64Decode = (str) => {
    try {
      return decodeURIComponent(atob(str));
    } catch {
      return str; // Return original if not Base64
    }
  };

  // Create a new post or update an existing one
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        const postRef = doc(db, 'blogs', editingPost.id);
        await updateDoc(postRef, prepareDataForFirestore(formData));
        setEditingPost(null);
        alert('Post updated successfully!');
      } else {
        const newPost = {
          createdAt: new Date().toISOString(),
          views: 0,
          ...prepareDataForFirestore(formData),
        };
        await addDoc(collection(db, 'blogs'), newPost);
        alert('Post created successfully!');
      }
      setFormData({
        title: '',
        excerpt: '',
        publishedDate: '',
        headerImage: '',
        author: '',
        timeToRead: '',
        views: 0,
        visibility: 'public',
        content: [],
        seoTitle: '',
        seoDescription: '',
        seoKeywords: '',
      });
      window.location.reload();
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Error saving post. Please try again.');
    }
  };

  // Begin editing an existing post
  const handleEdit = (post) => {
    setEditingPost(post);

    // Scroll to top to show we're entering edit mode
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    // Convert old list format to new format if needed
    const processedContent = (post.content || []).map(section => {
      if (section.type === 'list') {
        return {
          ...section,
          items: toArray(section.items)
        };
      }
      if (section.type === 'table') {
        return {
          ...section,
          headers: toArray(section.headers),
          rows: toArray(section.rows, true)
        };
      }
      if (section.type === 'timeline') {
        const events = toArray(section.events);
        return {
          ...section,
          events: events.map(event => ({
            ...event,
            content: event.content.map(contentSection => {
              if (contentSection.type === 'list') {
                return {
                  ...contentSection,
                  items: toArray(contentSection.items)
                };
              }
              return contentSection;
            })
          }))
        };
      }
      return section;
    });

    setFormData({
      title: post.title || '',
      excerpt: post.excerpt || '',
      publishedDate: post.publishedDate || '',
      headerImage: post.headerImage || '',
      author: post.author || '',
      timeToRead: post.timeToRead || '',
      views: post.views || 0,
      visibility: post.visibility || 'public',
      content: processedContent,
      seoTitle: post.seoTitle || post.title || '',
      seoDescription: post.seoDescription || post.excerpt || '',
      seoKeywords: post.seoKeywords || '',
    });
  };

  // Delete a post
  const handleDelete = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deleteDoc(doc(db, 'blogs', postId));
        alert('Post deleted successfully!');
        window.location.reload();
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Error deleting post. Please try again.');
      }
    }
  };

  // Copy the JSON for all posts to the clipboard
  const copyJSON = () => {
    const json = JSON.stringify(posts, null, 2);
    navigator.clipboard.writeText(json);
    alert('Blog posts JSON copied to clipboard!');
  };

  // Sort posts based on selected criteria
  const sortPosts = (postsToSort) => {
    const sortedPosts = [...postsToSort];

    switch (sortBy) {
      case 'newest':
        return sortedPosts.sort((a, b) => {
          const dateA = a.publishedDate ? new Date(a.publishedDate) : new Date(a.createdAt || 0);
          const dateB = b.publishedDate ? new Date(b.publishedDate) : new Date(b.createdAt || 0);
          return dateB - dateA;
        });
      case 'oldest':
        return sortedPosts.sort((a, b) => {
          const dateA = a.publishedDate ? new Date(a.publishedDate) : new Date(a.createdAt || 0);
          const dateB = b.publishedDate ? new Date(b.publishedDate) : new Date(b.createdAt || 0);
          return dateA - dateB;
        });
      case 'title':
        return sortedPosts.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      case 'author':
        return sortedPosts.sort((a, b) => (a.author || '').localeCompare(b.author || ''));
      case 'views':
        return sortedPosts.sort((a, b) => (b.views || 0) - (a.views || 0));
      default:
        return sortedPosts;
    }
  };

  // Migrate old posts from blogs.json to Firestore
  const migrateOldPosts = async () => {
    if (window.confirm('This will migrate posts from blogs.json to Firestore. Continue?')) {
      setMigrating(true);
      try {
        const response = await fetch('/blogs.json');
        const oldPosts = await response.json();
        
        const existingPosts = await getDocs(collection(db, 'blogs'));
        if (!existingPosts.empty) {
          const overwrite = window.confirm('There are already posts in Firestore. Do you want to continue anyway?');
          if (!overwrite) {
            setMigrating(false);
            return;
          }
        }

        const promises = oldPosts.map(async (post) => {
          const { id, ...postData } = post;
          return addDoc(collection(db, 'blogs'), {
            ...postData,
            views: 0,
            migratedAt: new Date().toISOString(),
            originalId: id
          });
        });

        await Promise.all(promises);
        alert(`Successfully migrated ${oldPosts.length} posts to Firestore!`);
        window.location.reload();
      } catch (error) {
        console.error('Error migrating posts:', error);
        alert('Error migrating posts. Please check the console for details.');
      } finally {
        setMigrating(false);
      }
    }
  };

  const renderContentEditor = () => (
    <div className="space-y-6">
      {/* Basic Post Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
            placeholder="Enter post title"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Author *</label>
          <input
            type="text"
            name="author"
            value={formData.author}
            onChange={handleChange}
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
            placeholder="Author name"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Excerpt</label>
        <textarea
          name="excerpt"
          value={formData.excerpt}
          onChange={handleChange}
          rows="3"
          className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
          placeholder="Brief description of the post"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Published Date</label>
          <input
            type="date"
            name="publishedDate"
            value={formData.publishedDate}
            onChange={handleChange}
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Header Image</label>
          <input
            type="text"
            name="headerImage"
            value={formData.headerImage}
            onChange={handleChange}
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
            placeholder="image.jpg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Read Time</label>
          <input
            type="text"
            name="timeToRead"
            value={formData.timeToRead}
            onChange={handleChange}
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
            placeholder="5 min"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Views</label>
          <input
            type="number"
            name="views"
            value={formData.views}
            onChange={handleChange}
            min="0"
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
            placeholder="0"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Visibility</label>
          <select
            name="visibility"
            value={formData.visibility}
            onChange={handleChange}
            className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
          >
            <option value="public">Public</option>
            <option value="unlisted">Unlisted</option>
            <option value="private">Private/Draft</option>
          </select>
        </div>
      </div>

      {/* Visibility Info */}
      <div className="bg-[#2a2a2a] p-4 rounded border border-[#4d4d4d]">
        <div className="flex items-center space-x-2 mb-2">
          <div className={`w-3 h-3 rounded-full ${
            formData.visibility === 'public' ? 'bg-green-500' : 
            formData.visibility === 'unlisted' ? 'bg-yellow-500' : 'bg-red-500'
          }`}></div>
          <span className="font-medium text-white">
            {formData.visibility === 'public' ? 'Public Post' : 
             formData.visibility === 'unlisted' ? 'Unlisted Post' : 'Private Draft'}
          </span>
        </div>
        <p className="text-sm text-gray-400">
          {formData.visibility === 'public' && 'Visible on blog page and accessible via direct link'}
          {formData.visibility === 'unlisted' && 'Hidden from blog page but accessible via direct link'}
          {formData.visibility === 'private' && 'Only visible in admin panel, not accessible publicly'}
        </p>
      </div>

      {/* Content Sections */}
      <div>
        <h3 className="text-xl font-bold text-white mb-4">Content Sections</h3>

        <div className="space-y-4">
          {formData.content.map((section, index) => (
            <div key={index} className="border border-[#4d4d4d] rounded-lg p-4 bg-[#1a1a1a]">
              <div className="flex justify-between items-center mb-3">
                <span className="font-medium text-white capitalize">{section.type} Section</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => moveSection(index, 'up')}
                    disabled={index === 0}
                    className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 px-2 py-1 rounded text-xs"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveSection(index, 'down')}
                    disabled={index === formData.content.length - 1}
                    className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 px-2 py-1 rounded text-xs"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteSection(index)}
                    className="bg-red-600 hover:bg-red-700 px-2 py-1 rounded text-xs"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <input
                  type="text"
                  value={section.title || ''}
                  onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
                  placeholder="Section title (optional)"
                  className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              {section.type === 'text' && (
                <div>
                  <textarea
                    value={section.value || ''}
                    onChange={(e) => handleSectionChange(index, 'value', e.target.value)}
                    placeholder="Enter text content (Markdown supported: **bold**, *italic*, `code`)"
                    rows="4"
                    className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Supports Markdown: **bold**, *italic*, `code`, [link](url), etc.
                  </p>
                </div>
              )}

              {section.type === 'image' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={section.fileName || ''}
                      onChange={(e) => handleSectionChange(index, 'fileName', e.target.value)}
                      placeholder="image.jpg"
                      className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                    />
                    <input
                      type="text"
                      value={section.alt || ''}
                      onChange={(e) => handleSectionChange(index, 'alt', e.target.value)}
                      placeholder="Alt text"
                      className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select
                      value={section.size || 'large'}
                      onChange={(e) => handleSectionChange(index, 'size', e.target.value)}
                      className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="small">Small Image (33%)</option>
                      <option value="medium">Medium Image (66%)</option>
                      <option value="large">Large Image (100%)</option>
                    </select>
                    <select
                      value={section.alignment || 'center'}
                      onChange={(e) => handleSectionChange(index, 'alignment', e.target.value)}
                      className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="left">Align Left</option>
                      <option value="center">Align Center</option>
                      <option value="right">Align Right</option>
                    </select>
                  </div>
                  <p className="text-xs text-gray-400">
                    Choose size and alignment for your image. Small/Medium images work best with left/right alignment.
                  </p>
                </div>
              )}

              {section.type === 'list' && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Bullet Points</span>
                    <button
                      type="button"
                      onClick={() => addListItem(index)}
                      className="bg-purple-600 hover:bg-purple-700 px-3 py-1 rounded text-sm"
                    >
                      + New Bullet
                    </button>
                  </div>
                  {(section.items || []).map((item, itemIndex) => (
                    <div key={itemIndex} className="flex gap-2">
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => moveListItem(index, itemIndex, 'up')}
                          disabled={itemIndex === 0}
                          className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 px-1 py-0.5 rounded text-xs"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveListItem(index, itemIndex, 'down')}
                          disabled={itemIndex === (section.items || []).length - 1}
                          className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 px-1 py-0.5 rounded text-xs"
                          title="Move down"
                        >
                          ↓
                        </button>
                      </div>
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => updateListItem(index, itemIndex, e.target.value)}
                        placeholder={`Bullet point ${itemIndex + 1} (supports **bold**, *italic*)`}
                        className="flex-1 p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeListItem(index, itemIndex)}
                        className="bg-red-600 hover:bg-red-700 px-2 py-1 rounded text-sm"
                        title="Remove item"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <p className="text-xs text-gray-400">
                    Each bullet point supports Markdown formatting: **bold**, *italic*, `code`, etc.
                  </p>
                </div>
              )}

              {section.type === 'table' && section.headers && section.rows && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Table Editor</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => addTableRow(index)}
                        className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-sm"
                      >
                        + Row
                      </button>
                      <button
                        type="button"
                        onClick={() => addTableColumn(index)}
                        className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded text-sm"
                      >
                        + Column
                      </button>
                    </div>
                  </div>
                  
                  <div className="border border-[#4d4d4d] rounded overflow-auto">
                    <table className="w-full border-collapse border border-[#4d4d4d]">
                      {/* Headers */}
                      <thead>
                        <tr className="bg-[#2a2a2a]">
                          {(Array.isArray(section.headers) ? section.headers : (() => {
                            const delimiter = section.headers.includes('|') ? '|' : ',';
                            return section.headers.split(delimiter).map(h => safeBase64Decode(h.trim()));
                          })()).map((header, colIndex) => (
                            <th key={colIndex} className="border border-[#4d4d4d] p-0 min-w-32">
                              <div className="flex">
                                <input
                                  type="text"
                                  value={Array.isArray(section.headers) ? header : safeBase64Decode(header)}
                                  onChange={(e) => updateTableCell(index, 0, colIndex, e.target.value, true)}
                                  className="flex-1 p-2 bg-transparent text-white border-0 focus:outline-none focus:bg-[#3a3a3a]"
                                  placeholder={`Header ${colIndex + 1}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeTableColumn(index, colIndex)}
                                  className="px-2 text-red-400 hover:bg-red-600 hover:text-white text-sm"
                                  title="Remove column"
                                >
                                  ×
                                </button>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      {/* Rows */}
                      <tbody>
                        {(Array.isArray(section.rows) ? section.rows : (() => {
                          const rowDelimiter = section.rows.includes('||') ? '||' : ';';
                          const cellDelimiter = section.rows.includes('|') && !section.rows.includes('||') ? '|' : ',';
                          return section.rows.split(rowDelimiter).map(row => 
                            row.split(cellDelimiter).map(cell => safeBase64Decode(cell.trim()))
                          );
                        })()).map((row, rowIndex) => (
                          <tr key={rowIndex} className="hover:bg-[#1a1a1a]">
                            {row.map((cell, colIndex) => (
                              <td key={colIndex} className="border border-[#4d4d4d] p-0">
                                <input
                                  type="text"
                                  value={Array.isArray(row) ? cell : safeBase64Decode(cell)}
                                  onChange={(e) => updateTableCell(index, rowIndex, colIndex, e.target.value)}
                                  className="w-full p-2 bg-transparent text-white border-0 focus:outline-none focus:bg-[#2a2a2a]"
                                  placeholder={`Cell ${rowIndex + 1},${colIndex + 1}`}
                                />
                              </td>
                            ))}
                            <td className="border border-[#4d4d4d] p-0 w-8">
                              <button
                                type="button"
                                onClick={() => removeTableRow(index, rowIndex)}
                                className="w-full h-full text-red-400 hover:bg-red-600 hover:text-white text-sm"
                                title="Remove row"
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-400">
                    Click on any cell to edit. Use + Row/+ Column buttons to expand the table.
                  </p>
                </div>
              )}

              {section.type === 'timeline' && section.events && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Timeline Events</span>
                    <button
                      type="button"
                      onClick={() => addTimelineEvent(index)}
                      className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-sm"
                    >
                      + New Event
                    </button>
            </div>

                  <div className="space-y-4">
                    {(Array.isArray(section.events) ? section.events : []).map((event, eventIndex) => (
                      <div key={eventIndex} className="border border-[#4d4d4d] rounded-lg p-4 bg-[#2a2a2a]">
                        <div className="flex justify-between items-center mb-3">
                          <span className="font-medium text-white">Event {eventIndex + 1}</span>
                          <button
                            type="button"
                            onClick={() => removeTimelineEvent(index, eventIndex)}
                            className="bg-red-600 hover:bg-red-700 px-2 py-1 rounded text-xs"
                          >
                            Remove Event
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                          <input
                            type="text"
                            value={event.date || ''}
                            onChange={(e) => updateTimelineEvent(index, eventIndex, 'date', e.target.value)}
                            placeholder="Date (e.g., 5000 BC, 2023, etc.)"
                            className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                          />
                          <input
                            type="text"
                            value={event.title || ''}
                            onChange={(e) => updateTimelineEvent(index, eventIndex, 'title', e.target.value)}
                            placeholder="Event title"
                            className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                          />
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-400">Event Content</span>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => addTimelineEventContent(index, eventIndex, 'text')}
                                className="bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded text-xs"
                              >
                                + Text
                              </button>
                              <button
                                type="button"
                                onClick={() => addTimelineEventContent(index, eventIndex, 'image')}
                                className="bg-green-600 hover:bg-green-700 px-2 py-1 rounded text-xs"
                              >
                                + Image
                              </button>
                              <button
                                type="button"
                                onClick={() => addTimelineEventContent(index, eventIndex, 'list')}
                                className="bg-purple-600 hover:bg-purple-700 px-2 py-1 rounded text-xs"
                              >
                                + List
                              </button>
                            </div>
                          </div>

                          <div className="space-y-3 ml-4">
                            {(event.content || []).map((contentSection, contentIndex) => (
                              <div key={contentIndex} className="border-l-2 border-[#4d4d4d] pl-3 bg-[#1a1a1a] p-3 rounded">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-xs text-gray-400 capitalize">{contentSection.type}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeTimelineEventContent(index, eventIndex, contentIndex)}
                                    className="bg-red-600 hover:bg-red-700 px-1 py-0.5 rounded text-xs"
                                  >
                                    ×
                                  </button>
                                </div>

                                {contentSection.type === 'text' && (
                                  <textarea
                                    value={contentSection.value || ''}
                                    onChange={(e) => updateTimelineEventContent(index, eventIndex, contentIndex, 'value', e.target.value)}
                                    placeholder="Enter text content"
                                    rows="2"
                                    className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500 text-sm"
                                  />
                                )}

                                {contentSection.type === 'image' && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <input
                                      type="text"
                                      value={contentSection.fileName || ''}
                                      onChange={(e) => updateTimelineEventContent(index, eventIndex, contentIndex, 'fileName', e.target.value)}
                                      placeholder="image.jpg"
                                      className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500 text-sm"
                                    />
                                    <input
                                      type="text"
                                      value={contentSection.alt || ''}
                                      onChange={(e) => updateTimelineEventContent(index, eventIndex, contentIndex, 'alt', e.target.value)}
                                      placeholder="Alt text"
                                      className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500 text-sm"
                                    />
                                  </div>
                                )}

                                {contentSection.type === 'list' && (
                                  <div className="space-y-2">
                                    {(Array.isArray(contentSection.items) ? contentSection.items : []).map((item, itemIndex) => (
                                      <input
                                        key={itemIndex}
                                        type="text"
                                        value={item}
                                        onChange={(e) => {
                                          const newItems = [...contentSection.items];
                                          newItems[itemIndex] = e.target.value;
                                          updateTimelineEventContent(index, eventIndex, contentIndex, 'items', newItems);
                                        }}
                                        placeholder={`Bullet point ${itemIndex + 1}`}
                                        className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500 text-sm"
                                      />
                                    ))}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newItems = [...(contentSection.items || []), ''];
                                        updateTimelineEventContent(index, eventIndex, contentIndex, 'items', newItems);
                                      }}
                                      className="bg-purple-600 hover:bg-purple-700 px-2 py-1 rounded text-xs"
                                    >
                                      + Bullet
                                    </button>
        </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-xs text-gray-400">
                    Add events to create a timeline. Each event can contain text, images, and bullet points.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSEOEditor = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">SEO Title</label>
        <input
          type="text"
          name="seoTitle"
          value={formData.seoTitle}
          onChange={handleChange}
          className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
          placeholder="Custom title for search engines (defaults to post title)"
        />
        <p className="text-xs text-gray-400 mt-1">
          {formData.seoTitle.length}/60 characters (recommended: 50-60)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">SEO Description</label>
        <textarea
          name="seoDescription"
          value={formData.seoDescription}
          onChange={handleChange}
          rows="3"
          className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
          placeholder="Description for search engines (defaults to excerpt)"
        />
        <p className="text-xs text-gray-400 mt-1">
          {formData.seoDescription.length}/160 characters (recommended: 150-160)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">SEO Keywords</label>
        <input
          type="text"
          name="seoKeywords"
          value={formData.seoKeywords}
          onChange={handleChange}
          className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
          placeholder="keyword1, keyword2, keyword3"
        />
        <p className="text-xs text-gray-400 mt-1">
          Separate keywords with commas
        </p>
      </div>

      <div className="bg-[#1a1a1a] p-4 rounded border border-[#4d4d4d]">
        <h4 className="font-bold text-white mb-2">Preview</h4>
        <div className="space-y-2">
          <div className="text-blue-500 text-lg">
            {formData.seoTitle || formData.title || 'Post Title'}
          </div>
          <div className="text-green-600 text-sm">
            https://kobypierce.com/blog/post-id
          </div>
          <div className="text-gray-300 text-sm">
            {formData.seoDescription || formData.excerpt || 'Post description will appear here...'}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreview = () => (
    <div className="bg-[#0d0d0d] p-6 rounded border border-[#4d4d4d]">
      {formData.title ? (
        <article className="text-white">
          {formData.headerImage && (
            <img
              src={`/vendor/${formData.headerImage}`}
              alt={formData.title}
              className="w-full h-auto rounded mb-4"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          <h1 className="text-4xl font-bold mb-2">{formData.title}</h1>
          <p className="text-gray-400 mb-4">
            By {formData.author || 'Unknown'} — {formData.timeToRead || ''} read
          </p>
          {formData.publishedDate && (
            <p className="text-gray-400 mb-4">
              Published on {new Date(formData.publishedDate).toLocaleDateString()}
            </p>
          )}
          {formData.excerpt && (
            <p className="text-gray-300 mb-6 italic">{formData.excerpt}</p>
          )}
          <div className="space-y-6">
            {formData.content.map((section, index) => (
              <div key={index}>
                {section.title && (
                  <h2 className="text-2xl font-bold mb-3">{section.title}</h2>
                )}
                {section.type === 'text' && (
                  <div className="prose prose-invert max-w-none">
                    <ReactMarkdown>{section.value}</ReactMarkdown>
                  </div>
                )}
                {section.type === 'image' && section.fileName && (
                  <div className={`${
                    section.size === 'small' ? 'w-1/3' : 
                    section.size === 'medium' ? 'w-2/3' : 
                    'w-full'
                  } ${
                    section.alignment === 'left' ? 'mr-auto' :
                    section.alignment === 'right' ? 'ml-auto' :
                    'mx-auto'
                  }`}>
                    <img
                      src={`/vendor/${section.fileName}`}
                      alt={section.alt || ''}
                      className="w-full h-auto rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                {section.type === 'list' && section.items && (
                  <ul className="list-disc ml-6 space-y-1">
                    {toArray(section.items).map((item, i) => (
                      <li key={i} className="text-gray-300">
                        <div className="prose prose-invert max-w-none inline">
                          <ReactMarkdown components={{
                            p: ({ children }) => <span>{children}</span>
                          }}>
                            {item}
                          </ReactMarkdown>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {section.type === 'table' && section.headers && section.rows && (
                  <table className="w-full border-collapse border border-[#4d4d4d]">
                    <thead>
                      <tr className="bg-[#1a1a1a]">
                        {toArray(section.headers).map((header, i) => (
                          <th key={i} className="border border-[#4d4d4d] p-3 text-left">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {toArray(section.rows, true).map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j} className="border border-[#4d4d4d] p-3">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {section.type === 'timeline' && section.events && (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#555555]"></div>

                    <div className="space-y-8">
                      {(Array.isArray(section.events) ? section.events : []).map((event, eventIndex) => (
                        <div key={eventIndex} className="relative flex">
                          {/* Timeline dot - positioned to center on the line */}
                          <div className="absolute left-6 -translate-x-1/2 z-10 w-8 h-8 bg-[#666666] rounded-full flex items-center justify-center text-white font-medium text-sm">
                            {eventIndex + 1}
              </div>

                          {/* Timeline content */}
                          <div className="flex-1 pb-8 ml-16">
                            <div className="bg-[#1a1a1a] rounded-lg p-4 border border-[#333333]">
                              <h3 className="text-xl font-semibold text-white mb-1">{event.title}</h3>
                              {event.date && (
                                <p className="text-gray-400 text-sm mb-3">{event.date}</p>
                              )}

                              <div className="space-y-3">
                                {(event.content || []).map((contentSection, contentIndex) => (
                                  <div key={contentIndex}>
                                    {contentSection.type === 'text' && (
                                      <div className="prose prose-invert max-w-none">
                                        <ReactMarkdown>{contentSection.value}</ReactMarkdown>
                                      </div>
                                    )}

                                    {contentSection.type === 'image' && contentSection.fileName && (
                                      <img
                                        src={`/vendor/${contentSection.fileName}`}
                                        alt={contentSection.alt || ''}
                                        className="w-full h-auto rounded"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    )}

                                    {contentSection.type === 'list' && contentSection.items && (
                                      <ul className="list-disc ml-6 space-y-1">
                                        {toArray(contentSection.items).map((item, i) => (
                                          <li key={i} className="text-gray-300">
                                            <div className="prose prose-invert max-w-none inline">
                                              <ReactMarkdown components={{
                                                p: ({ children }) => <span>{children}</span>
                                              }}>
                                                {item}
                                              </ReactMarkdown>
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </article>
      ) : (
        <p className="text-gray-400 text-center py-8">
          Start writing your post to see the preview
        </p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white admin-page">
      <AdminHeader />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Blog Editor</h1>

        {/* Edit Mode Indicator */}
        {editingPost && (
          <div className="mb-6 p-4 bg-blue-600/20 border border-blue-500/50 rounded-lg animate-in fade-in duration-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                <div>
                  <h3 className="font-semibold text-blue-400">Editing Post</h3>
                  <p className="text-sm text-gray-300">
                    Currently editing: <span className="font-medium text-white">"{editingPost.title}"</span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingPost(null);
                  setFormData({
                    title: '',
                    excerpt: '',
                    publishedDate: '',
                    headerImage: '',
                    author: '',
                    timeToRead: '',
                    views: 0,
                    visibility: 'public',
                    content: [],
                    seoTitle: '',
                    seoDescription: '',
                    seoKeywords: '',
                  });
                  setActiveTab('content');
                }}
                className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-sm rounded transition-colors"
              >
                Cancel Edit
              </button>
            </div>
          </div>
        )}

        {/* Editor Form */}
        <form onSubmit={handleSubmit} className="mb-8">
          <div className="bg-[#1a1a1a] rounded-lg border border-[#4d4d4d] overflow-hidden">
            {/* Tab Navigation */}
            <div className="flex border-b border-[#4d4d4d]">
              <button
                type="button"
                onClick={() => setActiveTab('content')}
                className={`px-6 py-3 font-medium ${
                  activeTab === 'content'
                    ? 'bg-blue-600 text-white'
                    : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                }`}
              >
                Content
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('seo')}
                className={`px-6 py-3 font-medium ${
                  activeTab === 'seo'
                    ? 'bg-blue-600 text-white'
                    : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                }`}
              >
                SEO
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('preview')}
                className={`px-6 py-3 font-medium ${
                  activeTab === 'preview'
                    ? 'bg-blue-600 text-white'
                    : 'bg-[#2a2a2a] text-gray-300 hover:bg-[#3a3a3a]'
                }`}
              >
                Preview
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'content' && renderContentEditor()}
              {activeTab === 'seo' && renderSEOEditor()}
              {activeTab === 'preview' && renderPreview()}
            </div>
          </div>

          {/* Add Section Buttons and Submit Button */}
          <div className="mt-6 flex justify-between items-center">
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => addSection('text')}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded font-medium"
              >
                + Text
              </button>
              <button
                type="button"
                onClick={() => addSection('image')}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded font-medium"
              >
                + Image
              </button>
              <button
                type="button"
                onClick={() => addSection('list')}
                className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded font-medium"
              >
                + List
              </button>
              <button
                type="button"
                onClick={() => addSection('table')}
                className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded font-medium"
              >
                + Table
              </button>
              <button
                type="button"
                onClick={() => addSection('timeline')}
                className="bg-cyan-600 hover:bg-cyan-700 px-4 py-2 rounded font-medium"
              >
                + Timeline
              </button>
            </div>
            
            <button
              type="submit"
              className="bg-green-600 hover:bg-green-700 px-6 py-3 rounded font-medium"
            >
              {editingPost ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>

        {/* Existing Posts */}
        <div className="bg-[#1a1a1a] rounded-lg border border-[#4d4d4d] p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Existing Posts</h2>
            <div className="flex gap-3">
              <button
                onClick={copyJSON}
                className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded"
              >
                Copy JSON
              </button>
              <button
                onClick={migrateOldPosts}
                disabled={migrating}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-800 px-4 py-2 rounded"
              >
                {migrating ? 'Migrating...' : 'Migrate Old Posts'}
              </button>
            </div>
          </div>

          {/* Sorting Controls */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-[#2a2a2a] rounded border border-[#4d4d4d]">
            <span className="text-sm font-medium text-gray-300">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-[#212121] border border-[#4d4d4d] rounded px-3 py-2 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="title">Title (A-Z)</option>
              <option value="author">Author (A-Z)</option>
              <option value="views">Most Viewed</option>
            </select>
            <span className="text-sm text-gray-400">
              {posts.length} post{posts.length !== 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <p>Loading posts...</p>
          ) : posts.length === 0 ? (
            <p>No posts available.</p>
          ) : (
            <div className="space-y-4">
              {sortPosts(posts).map(post => (
                <div key={post.id} className="border border-[#4d4d4d] rounded p-4 flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold">{post.title}</h3>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${
                          post.visibility === 'public' ? 'bg-green-500' : 
                          post.visibility === 'unlisted' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <span className="text-xs text-gray-400 uppercase">
                          {post.visibility || 'public'}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mb-2">
                      By {post.author} — {post.timeToRead} read — {post.views || 0} views
                    </p>
                    {post.excerpt && (
                      <p className="text-gray-300 text-sm">{post.excerpt}</p>
                    )}
                    {post.migratedAt && (
                      <p className="text-yellow-400 text-xs mt-1">
                        Migrated from old system
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(post)}
                      className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBlog;